/**
 * ArgusTableController — main Stimulus controller that wires all Argus Table
 * sub-modules together and exposes the public API.
 *
 * Registration: "argus--table"
 * File: app/javascript/controllers/argus/table_controller.js
 */

import { Controller } from "@hotwired/stimulus"
import * as XLSX from 'xlsx'
import { DataManager } from '../../lib/argus_table/data_manager'
import { SearchEngine } from '../../lib/argus_table/search_engine'
import { SortManager } from '../../lib/argus_table/sort_manager'
import { PaginationManager } from '../../lib/argus_table/pagination_manager'
import { ColumnToggler } from '../../lib/argus_table/column_toggler'
import { DetailViewManager } from '../../lib/argus_table/detail_view_manager'
import { Renderer } from '../../lib/argus_table/renderer'
import { resolveField } from '../../lib/argus_table/utils'

let _tableIdCounter = 0

// Named constant — single source for the loading-overlay grace period.
// The overlay is only drawn after a fetch has been in flight this long,
// so fast loads never flash a spinner. (Requirement 10)
const LOADING_SHOW_DELAY_MS = 150

export default class extends Controller {
  static targets = ["table", "toolbar", "search", "pagination"]

  static values = {
    tableDefinition: Array,
    data: Array,
    pageSize: { type: Number, default: 50 },
    pageList: { type: Array, default: [10, 25, 50, 100] },
    serverPagination: { type: Boolean, default: false },
    buttons: { type: Array, default: [] },
    showRefresh: { type: Boolean, default: false },
    showExport: { type: Boolean, default: false },
    detailView: { type: Boolean, default: false },
    rowAttributes: String,
  }

  // ── Lifecycle ────────────────────────────────────────────────────

  connect() {
    this._searchQuery = ''
    this._columns = this._parseColumns()
    this._tableId = `argus-table-${++_tableIdCounter}`

    // ── Loading overlay state ──
    // Whether a fetch is currently in flight for this table.
    this._loading = false
    // Pending Show_Delay timer id (or null when none is scheduled).
    this._loadingTimer = null
    // Whether the overlay is currently visible on screen.
    this._overlayVisible = false
    // Reference to the overlay DOM node. Populated by task 1.2's
    // _ensureLoadingHost(); until then the loading helpers guard against
    // its absence so the API is safe to call before the host exists.
    this._loadingOverlay = null

    // Instantiate sub-modules
    this.dataManager = new DataManager(() => this._render())

    this.sortManager = new SortManager()

    this.paginationManager = new PaginationManager({
      pageSize: this.pageSizeValue,
      pageList: this.pageListValue,
      serverPagination: this.serverPaginationValue,
    })

    this.columnToggler = new ColumnToggler(this._columns, () => this._render())

    // Detail view manager (only if enabled)
    this.detailViewManager = null
    if (this.detailViewValue) {
      this.detailViewManager = this._createDetailViewManager()
    }

    // Row attributes function
    this._rowAttributesFn = this._resolveRowAttributesFn()

    // Build the sibling loading host + overlay once, so the overlay node
    // exists before any load and survives _render() innerHTML rebuilds.
    this._ensureLoadingHost()

    // Initial render
    this._render()

    // Listen for inline editable save events
    this._editableSaveHandler = (e) => {
      if (typeof this._onEditableSave === 'function') {
        this._onEditableSave(e.detail)
      }
    }
    this.element.addEventListener('argus-table:editable-save', this._editableSaveHandler)

    // Load initial data if provided via Stimulus value
    if (this.hasDataValue && Array.isArray(this.dataValue) && this.dataValue.length > 0) {
      this.dataManager.load(this.dataValue)
    }
  }

  disconnect() {
    // Cancel any pending Show_Delay timer so it can't fire after teardown.
    if (this._loadingTimer) {
      clearTimeout(this._loadingTimer)
      this._loadingTimer = null
    }

    // Clean up
    this.dataManager = null
    this.sortManager = null
    this.paginationManager = null
    this.columnToggler = null
    this.detailViewManager = null
  }

  // ── Stimulus value callbacks ─────────────────────────────────────

  dataValueChanged() {
    if (this.dataManager && this.hasDataValue) {
      this.dataManager.load(this.dataValue)
    }
  }

  // ── Public API (migration-compatible) ────────────────────────────

  /**
   * Replace all table data.
   * @param {Array<Object>} rows
   */
  loadData(rows) {
    this.dataManager.load(rows)
  }

  /**
   * Append rows to the existing dataset.
   * @param {Array<Object>} rows
   */
  appendData(rows) {
    this.dataManager.append(rows)
  }

  /**
   * Remove rows where row[field] === value.
   * @param {string} field
   * @param {*} value
   */
  removeRow(field, value) {
    this.dataManager.remove(field, value)
  }

  /**
   * Update the row matching the given id.
   *
   * Single public update method. The patch-vs-render decision is made
   * internally: the merge is applied to the DataManager (source of truth)
   * first, then exactly one repaint happens — an in-place cell patch on the
   * common path, or a full `_render()` when ordering/visibility would change
   * (or when the id matches no stored row).
   *
   * @param {*} id
   * @param {Object} rowData
   */
  updateRow(id, rowData) {
    this._updateRowInPlaceOrRender(id, rowData)
  }

  /**
   * Return a copy of the current dataset.
   * @returns {Array<Object>}
   */
  getData() {
    return this.dataManager.getAll()
  }

  /**
   * Remove all rows.
   */
  clearData() {
    this.dataManager.clear()
  }

  /**
   * Show a column by field name.
   * @param {string} fieldName
   */
  showColumn(fieldName) {
    this.columnToggler.show(fieldName)
  }

  /**
   * Hide a column by field name.
   * @param {string} fieldName
   */
  hideColumn(fieldName) {
    this.columnToggler.hide(fieldName)
  }

  /**
   * Server pagination: append rows and maintain scroll position.
   * @param {Array<Object>} rows
   */
  loadMore(rows) {
    const scrollTop = this.element.scrollTop
    this.dataManager.append(rows)
    // Restore scroll position after render
    requestAnimationFrame(() => {
      this.element.scrollTop = scrollTop
    })
  }

  // ── Update path: patch-in-place vs. full render ──────────────────

  /**
   * Orchestrate a single `updateRow` call.
   *
   * Flow (design.md → Algorithmic Pseudocode → updateRow):
   *   1. Merge `rowData` into the DataManager silently (source of truth) —
   *      this does NOT fire onChange, so it never triggers a redundant
   *      full render.
   *   2. If no row matched (`updateSilently` returned null), run a defensive
   *      `_render()` so the DOM stays consistent with the data.
   *   3. Otherwise decide patch-vs-render via `_shouldPatchInPlace`:
   *        - patch path: locate the rendered `<tr data-argus-row-id>` and
   *          delegate to `Renderer.patchRow`;
   *        - fallback path: run the existing `_render()`.
   *
   * Guarantees exactly one repaint: either `patchRow` or `_render`, never
   * both and never zero when a visible row changed.
   *
   * @param {*} id
   * @param {Object} rowData
   * @private
   */
  _updateRowInPlaceOrRender(id, rowData) {
    const snapshot = this.dataManager.updateSilently(id, rowData)

    // Unknown id — nothing merged. A defensive full render keeps the DOM
    // consistent with the (unchanged) data. (Req 2.6, 6.6)
    if (!snapshot) {
      this._render()
      return
    }

    const { oldRow, newRow } = snapshot

    if (this._shouldPatchInPlace(id, oldRow, newRow)) {
      const tr = this._findRenderedRowEl(id)
      if (!tr) {
        // In the dataset but not currently rendered. `_affectsPageMembership`
        // already forces `_render` for non-rendered rows, so this is a
        // defensive no-op; the merged data is reflected on the next render.
        return
      }
      const index = this._renderedRowIndex(tr)
      Renderer.patchRow(tr, newRow, this.columnToggler.getVisibleColumns(), index)
    } else {
      // Ordering/visibility would change → full rebuild. (Req 2.1, 2.2, 2.3)
      this._render()
    }
  }

  /**
   * Decide whether an `updateRow` can be satisfied by an in-place cell patch.
   *
   * Returns `true` only when the change is provably safe to patch: it leaves
   * the row's page membership, sort position, and filter match exactly as
   * rendered. Any of the three checks returning `true` forces a full
   * `_render()` instead (ordering/visibility would change).
   *
   * The `id` is threaded through so `_affectsPageMembership` can look up the
   * rendered `<tr data-argus-row-id>` — see design.md → Algorithmic
   * Pseudocode (`_affectsPageMembership` uses `idOfRowBeingUpdated`).
   *
   * @param {*} id - The id of the row being updated.
   * @param {Object} oldRow
   * @param {Object} newRow
   * @returns {boolean}
   * @private
   */
  _shouldPatchInPlace(id, oldRow, newRow) {
    if (this._affectsPageMembership(id)) return false
    if (this._affectsSort(oldRow, newRow)) return false
    if (this._affectsFilter(oldRow, newRow)) return false
    return true
  }

  /**
   * Whether the update changes the row's position under the ACTIVE sort.
   *
   * A change can only move a row when the value of the active sort column
   * changed. When there is no active sort (`field`/`direction` are null) the
   * order is fixed, so the update can never reorder — return `false`.
   *
   * Values are compared with the SAME coercion `SortManager._defaultCompare`
   * uses (`String(a).localeCompare(String(b))`), so `7` and `"7"` are treated
   * as equal — consistent with how the sorter itself would order them.
   *
   * @param {Object} oldRow
   * @param {Object} newRow
   * @returns {boolean}
   * @private
   */
  _affectsSort(oldRow, newRow) {
    const sort = this.sortManager.currentSort

    // No active sort → order cannot change.
    if (!sort || !sort.field || !sort.direction) return false

    const oldVal = resolveField(oldRow, sort.field)
    const newVal = resolveField(newRow, sort.field)

    return !this._sortValuesEqual(oldVal, newVal)
  }

  /**
   * Compare two values using the same semantics as `SortManager._defaultCompare`
   * (locale-aware string comparison). Two values are "equal" for sort purposes
   * when the default comparator returns 0 — so e.g. `7` and `"7"` are equal.
   *
   * @param {*} a
   * @param {*} b
   * @returns {boolean}
   * @private
   */
  _sortValuesEqual(a, b) {
    return String(a).localeCompare(String(b)) === 0
  }

  /**
   * Whether the update flips the row's match against the ACTIVE search query.
   *
   * When the query is empty, every row is visible regardless of its values, so
   * no update can change visibility — return `false`. Otherwise reuse
   * `SearchEngine.filter` on a single-row array for both the old and new row
   * so the match logic is byte-identical to what `_render` uses, and return
   * `true` only when the match flips (was matching → no longer matches, or
   * vice-versa).
   *
   * @param {Object} oldRow
   * @param {Object} newRow
   * @returns {boolean}
   * @private
   */
  _affectsFilter(oldRow, newRow) {
    const query = this._searchQuery

    // No active filter → all rows visible; match can't flip.
    if (!query || query.trim() === '') return false

    const oldMatch =
      SearchEngine.filter([oldRow], query, this._columns, null).length === 1
    const newMatch =
      SearchEngine.filter([newRow], query, this._columns, null).length === 1

    return oldMatch !== newMatch
  }

  /**
   * Whether the target row is NOT on the currently rendered page (so a full
   * `_render` is required).
   *
   * Patching is only possible when there is a rendered `<tr>` to patch. If the
   * row is not on the current page there is no visible node, and a full
   * `_render()` is the correct (and cheap) way to reflect any change in page
   * composition. Mirrors design.md → `_affectsPageMembership`: return `true`
   * when `_findRenderedRowEl(id)` is null.
   *
   * @param {*} id - The id of the row being updated.
   * @returns {boolean}
   * @private
   */
  _affectsPageMembership(id) {
    return this._findRenderedRowEl(id) === null
  }

  /**
   * Find the rendered `<tr>` for a row by its `data-argus-row-id` anchor.
   *
   * @param {*} id - The row id (matched as a string against the attribute)
   * @returns {HTMLTableRowElement|null}
   * @private
   */
  _findRenderedRowEl(id) {
    if (!this.element || id == null) return null
    const selector = `tr[data-argus-row-id="${this._cssEscape(String(id))}"]`
    return this.element.querySelector(selector)
  }

  /**
   * Index of a rendered row element among its sibling body rows, for passing
   * to formatters via `patchRow`. Falls back to 0 when it can't be resolved.
   *
   * @param {HTMLTableRowElement} tr
   * @returns {number}
   * @private
   */
  _renderedRowIndex(tr) {
    const tbody = tr && tr.parentNode
    if (!tbody || typeof tbody.querySelectorAll !== 'function') return 0
    const rows = Array.prototype.slice.call(tbody.querySelectorAll('tr[data-argus-row-id]'))
    const idx = rows.indexOf(tr)
    return idx === -1 ? 0 : idx
  }

  /**
   * Escape a string for safe use inside a CSS attribute-selector value.
   * Prefers the native `CSS.escape` and falls back to escaping the characters
   * that matter for a quoted attribute selector.
   *
   * @param {string} value
   * @returns {string}
   * @private
   */
  _cssEscape(value) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(value)
    }
    return value.replace(/["\\]/g, '\\$&')
  }

  // ── Loading API ──────────────────────────────────────────────────

  /**
   * Build the loading overlay node once, up front.
   *
   * IMPORTANT: we do NOT move/reparent this.element. An earlier version wrapped
   * this.element in a new host <div> (insertBefore + appendChild), but moving a
   * Stimulus-controlled element in the DOM makes Stimulus disconnect and
   * reconnect every controller on that element and its subtree. On pages where
   * a sibling controller targets this element (e.g. table--users-table's
   * `table` target on #usersTable), that re-connection churn broke the sibling's
   * initial render and the table came up empty.
   *
   * Instead we make this.element itself the positioning context (position:
   * relative) and append the overlay as a direct CHILD of this.element. Because
   * _render() clears this.element via innerHTML = '', it would destroy a child
   * overlay — so _render() re-appends the overlay after each rebuild (see
   * _reattachOverlay), and the overlay node is preserved across renders.
   *
   * Idempotent: only builds the overlay once. Safe to call repeatedly.
   */
  _ensureLoadingHost() {
    if (!this.element) return
    // Already built — nothing to do.
    if (this._loadingOverlay) return

    // Make the table element the positioning context for the absolutely
    // positioned overlay without moving it in the DOM.
    this.element.classList.add('argus-table-loading-host')

    const overlay = document.createElement('div')
    overlay.className = 'argus-table-loading-overlay'
    overlay.setAttribute('role', 'status')
    overlay.setAttribute('aria-live', 'polite')
    overlay.innerHTML =
      '<div class="spinner-border" role="status">' +
      '<span class="visually-hidden">Loading table data…</span>' +
      '</div>'

    this._loadingOverlay = overlay
    this.element.appendChild(overlay)
  }

  /**
   * Re-attach the overlay node as a child of this.element after a render.
   *
   * _render() rebuilds this.element via innerHTML = '', which removes the
   * overlay. This restores it (preserving its current visibility state) so the
   * overlay survives every render. Idempotent and safe when no overlay exists.
   */
  _reattachOverlay() {
    if (!this.element || !this._loadingOverlay) return
    if (this._loadingOverlay.parentNode !== this.element) {
      this.element.appendChild(this._loadingOverlay)
    }
  }

  /**
   * Signal that an asynchronous data fetch has begun.
   *
   * Sets aria-busy immediately (so assistive tech reflects the true
   * Loading_State right away) and schedules the visual overlay to appear
   * only after LOADING_SHOW_DELAY_MS, so fast loads never flash a spinner.
   *
   * Idempotent — safe to call repeatedly; early-returns if a timer is
   * already pending or the overlay is already visible.
   */
  showLoading() {
    this._loading = true
    // Immediate, independent of the Show_Delay (Req 6.1, 10.5)
    if (this.element) this.element.setAttribute('aria-busy', 'true')

    if (this._loadingTimer || this._overlayVisible) return

    this._loadingTimer = setTimeout(() => {
      this._loadingTimer = null
      // Only draw the overlay if we are still loading (Req 10.3)
      if (this._loading) this._showOverlay()
    }, LOADING_SHOW_DELAY_MS)
  }

  /**
   * Signal that an asynchronous data fetch has ended (success or error).
   *
   * Cancels any pending Show_Delay timer (fast fetch — no overlay ever
   * shown), removes the overlay if visible, and clears aria-busy.
   *
   * Idempotent and safe to call when no overlay is showing.
   */
  hideLoading() {
    this._loading = false

    if (this._loadingTimer) {
      // Fast fetch — cancel the pending display so no overlay appears
      // (Req 10.2, 10.4)
      clearTimeout(this._loadingTimer)
      this._loadingTimer = null
    }

    this._hideOverlay()

    if (this.element) this.element.removeAttribute('aria-busy')  // (Req 6.2)
  }

  /**
   * Reveal the overlay and dim the underlying table content.
   * Guards against a missing overlay node (created later by task 1.2).
   */
  _showOverlay() {
    if (this._loadingOverlay) {
      this._loadingOverlay.classList.add('is-visible')
    }
    if (this.element) {
      this.element.classList.add('argus-table-content-dimmed')
    }
    this._overlayVisible = true
  }

  /**
   * Hide the overlay and undim the table content.
   * Guards against a missing overlay node (created later by task 1.2).
   */
  _hideOverlay() {
    if (this._loadingOverlay) {
      this._loadingOverlay.classList.remove('is-visible')
    }
    if (this.element) {
      this.element.classList.remove('argus-table-content-dimmed')
    }
    this._overlayVisible = false
  }

  // ── Render pipeline ──────────────────────────────────────────────

  /**
   * Execute the full data pipeline and rebuild the DOM.
   *
   * Pipeline: allRows → search filter → sort → paginate → render
   */
  _render() {
    if (!this.element) return

    this.element.setAttribute('aria-busy', 'true')

    try {
      const allRows = this.dataManager.getAll()
      const columns = this._columns
      const visibleColumns = this.columnToggler.getVisibleColumns()

      // Search filter
      let filteredRows
      if (this._searchQuery && this._searchQuery.trim() !== '') {
        // Try to get the current tbody for DOM-based search (select elements)
        const currentTbody = this.element.querySelector('tbody')
        filteredRows = SearchEngine.filter(allRows, this._searchQuery, columns, currentTbody)
      } else {
        filteredRows = allRows
      }

      // Sort
      const sortedRows = this.sortManager.sort(filteredRows, columns)

      // Paginate
      const paginationResult = this.paginationManager.paginate(sortedRows)

      // Clear and rebuild DOM
      this.element.innerHTML = ''

      // Toolbar
      const toolbar = Renderer.renderToolbar({
        searchEnabled: true,
        tableId: this._tableId,
        buttons: this.buttonsValue,
        columnTogglerDropdown: this.columnToggler.renderDropdown(),
        showRefresh: this.showRefreshValue,
        showExport: this.showExportValue,
        onSearch: (query) => this._onSearch(query),
        onRefresh: () => this._onRefresh(),
        onExport: () => this._onExport(),
      })
      this.element.appendChild(toolbar)

      // Restore search input value and focus after re-render
      if (this._searchQuery) {
        const searchInput = toolbar.querySelector('input[type="text"]')
        if (searchInput) {
          searchInput.value = this._searchQuery
          requestAnimationFrame(() => {
            searchInput.focus()
            searchInput.setSelectionRange(
              this._searchQuery.length,
              this._searchQuery.length
            )
          })
        }
      }

      // Table
      const table = Renderer.renderTable({
        columns: columns,
        visibleColumns: visibleColumns,
        pageRows: paginationResult.pageRows,
        totalRows: paginationResult.totalRows,
        from: paginationResult.from,
        to: paginationResult.to,
        currentPage: paginationResult.currentPage,
        totalPages: paginationResult.totalPages,
        sortState: this.sortManager.currentSort,
        tableId: this._tableId,
        detailView: this.detailViewValue,
        detailViewManager: this.detailViewManager,
        onSort: (field) => this._onSort(field),
        onDetailToggle: (index, row, tr) => this._onDetailToggle(index, row, tr),
        rowAttributesFn: this._rowAttributesFn,
      })
      this.element.appendChild(table)

      // Pagination
      const pagination = Renderer.renderPagination({
        currentPage: paginationResult.currentPage,
        totalPages: paginationResult.totalPages,
        pageSize: this.paginationManager.pageSize,
        pageList: this.paginationManager.pageList,
        totalRows: paginationResult.totalRows,
        from: paginationResult.from,
        to: paginationResult.to,
        onPageChange: (page) => this._onPageChange(page),
        onPageSizeChange: (size) => this._onPageSizeChange(size),
        serverPagination: this.serverPaginationValue,
        hasMore: this.paginationManager.hasMore,
      })
      this.element.appendChild(pagination)
    } finally {
      // The rebuild above cleared this.element via innerHTML, which removed the
      // overlay node (it is a direct child of this.element). Re-append it so the
      // overlay survives every render. _reattachOverlay is idempotent.
      this._reattachOverlay()

      // The synchronous rebuild above set aria-busy for the duration of the
      // render. Normally we clear it here — but if a fetch is still in flight
      // (this._loading), the fetch-scoped busy state owns aria-busy and must
      // NOT be cleared by this transient per-render toggle. Re-assert it
      // instead so assistive tech still reflects the true Loading_State.
      // (Req 1.2, 6.1, 10.3 — idempotent.)
      if (this._loading) {
        this.element.setAttribute('aria-busy', 'true')
        // If the Show_Delay already elapsed and the overlay was visible before
        // this interim render, re-assert its visible state (the overlay was
        // just re-attached above; here we ensure its visibility/dimming classes
        // are still applied). _showOverlay() is idempotent.
        if (this._overlayVisible) {
          this._showOverlay()
        }
      } else {
        this.element.removeAttribute('aria-busy')
      }

      // Fire post-render callback if registered (used by items table for TomSelect init)
      if (typeof this._onAfterRender === 'function') {
        requestAnimationFrame(() => this._onAfterRender())
      }
    }
  }

  // ── Internal event handlers ──────────────────────────────────────

  _onSearch(query) {
    this._searchQuery = query
    this.paginationManager.goToPage(1)
    this._render()
  }

  _onSort(field) {
    this.sortManager.nextSortState(field)
    this.paginationManager.goToPage(1)
    this._render()
  }

  _onPageChange(page) {
    if (this.serverPaginationValue) {
      const direction = page > this.paginationManager.currentPage ? 'next' : 'prev'
      this.element.dispatchEvent(
        new CustomEvent('argus-table:page-request', {
          bubbles: true,
          detail: { direction, currentPage: this.paginationManager.currentPage },
        })
      )
    }
    this.paginationManager.goToPage(page)
    this._render()
  }

  _onPageSizeChange(size) {
    this.paginationManager.setPageSize(size)
    this._render()
  }

  _onRefresh() {
    this.element.dispatchEvent(
      new CustomEvent('argus-table:refresh', { bubbles: true })
    )
  }

  _onExport() {
    this._exportToXlsx()
  }

  _onDetailToggle(index, row, tr) {
    if (this.detailViewManager) {
      this.detailViewManager.toggleRow(index, row, tr)
    }
  }

  // ── Column definition parsing ────────────────────────────────────

  /**
   * Parse column definitions from the tableDefinition Stimulus value,
   * or fall back to reading data attributes from <th> elements.
   *
   * @returns {Array<Object>} Column definitions
   */
  _parseColumns() {
    // Prefer tableDefinition Stimulus value when present
    if (this.hasTableDefinitionValue && Array.isArray(this.tableDefinitionValue) && this.tableDefinitionValue.length > 0) {
      return this.tableDefinitionValue.map((col) => ({
        field: col.field || '',
        title: col.title || col.field || '',
        sortable: col.sortable === true || col.sortable === 'true',
        visible: col.visible !== false,
        editable: col.editable === true || col.editable === 'true',
        formatter: col.formatter || null,
        sorter: col.sorter || null,
        class: col.class || null,
        width: col.width || null,
        // Optional deferred-cell placeholder token (e.g. "…"); passed through
        // unchanged so the Renderer can substitute it for null/undefined values
        // on the non-formatter text path. (Requirement 5)
        placeholder: col.placeholder != null ? col.placeholder : null,
      }))
    }

    // Fall back to <th> data attributes
    return this._parseColumnsFromMarkup()
  }

  /**
   * Parse column definitions from <th> elements in the controller's DOM.
   *
   * Reads: data-field, data-sortable, data-formatter, data-sorter, data-class, data-width
   *
   * @returns {Array<Object>}
   */
  _parseColumnsFromMarkup() {
    const thElements = this.element.querySelectorAll('th')
    const columns = []

    thElements.forEach((th) => {
      const field = th.dataset.field
      if (!field) return

      columns.push({
        field: field,
        title: th.textContent.trim() || field,
        sortable: th.dataset.sortable === 'true',
        visible: true,
        formatter: th.dataset.formatter || null,
        sorter: th.dataset.sorter || null,
        class: th.dataset.class || null,
        width: th.dataset.width || null,
        // Optional deferred-cell placeholder token from data-placeholder.
        // (Requirement 5)
        placeholder: th.dataset.placeholder != null ? th.dataset.placeholder : null,
      })
    })

    return columns
  }

  // ── Detail view helpers ──────────────────────────────────────────

  /**
   * Create a DetailViewManager, resolving detailFilter and detailFormatter
   * from window if they are string names.
   *
   * @returns {DetailViewManager}
   */
  _createDetailViewManager() {
    let detailFilter = () => false
    let detailFormatter = () => ''

    // Resolve from data attributes or window
    const filterName = this.element.dataset.detailFilter
    if (filterName && typeof window[filterName] === 'function') {
      detailFilter = window[filterName]
    }

    const formatterName = this.element.dataset.detailFormatter
    if (formatterName && typeof window[formatterName] === 'function') {
      detailFormatter = window[formatterName]
    }

    return new DetailViewManager({ detailFilter, detailFormatter })
  }

  // ── Row attributes helper ────────────────────────────────────────

  /**
   * Resolve the rowAttributes function from the Stimulus value.
   * The value is a string name of a function on window.
   *
   * @returns {Function|null}
   */
  _resolveRowAttributesFn() {
    if (!this.hasRowAttributesValue || !this.rowAttributesValue) {
      return null
    }

    const fn = window[this.rowAttributesValue]
    if (typeof fn === 'function') {
      return fn
    }

    console.warn(
      `[ArgusTable] rowAttributes function "${this.rowAttributesValue}" not found on window`
    )
    return null
  }

  // ── Export functionality ─────────────────────────────────────────

  /**
   * Export current filtered + visible data to XLSX.
   * Uses the XLSX library available on window (loaded via application.js).
   */
  _exportToXlsx() {
    try {
      if (!XLSX || !XLSX.utils) {
        console.warn('[ArgusTable] XLSX library not available, export aborted')
        return
      }

      const visibleColumns = this.columnToggler.getVisibleColumns()
      const allRows = this.dataManager.getAll()

      // Apply current search filter
      let exportRows
      if (this._searchQuery && this._searchQuery.trim() !== '') {
        exportRows = SearchEngine.filter(allRows, this._searchQuery, this._columns, null)
      } else {
        exportRows = allRows
      }

      // Build worksheet data: header row + data rows
      const headers = visibleColumns.map((col) => col.title || col.field)
      const dataRows = exportRows.map((row) =>
        visibleColumns.map((col) => {
          const value = resolveField(row, col.field)
          return value != null ? value : ''
        })
      )

      const wsData = [headers, ...dataRows]
      const ws = XLSX.utils.aoa_to_sheet(wsData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Export')
      XLSX.writeFile(wb, 'table-export.xlsx')
    } catch (e) {
      console.warn('[ArgusTable] Export failed:', e)
    }
  }
}
